/**
 * useWebSocket - React hook for real-time map collaboration
 *
 * Provides WebSocket connection to the map Durable Object with:
 * - Automatic reconnection with exponential backoff
 * - Ping/pong keepalive
 * - State synchronization
 * - Optimistic updates with server confirmation
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useMapStore } from '../store/mapStore';
import { useAuthStore } from '../store/authStore';
import { wsUrl } from '../lib/config';

// Message types matching the Durable Object protocol
interface WSMessage {
  type: 'SYNC' | 'UPDATE' | 'PRESENCE' | 'ERROR' | 'PING' | 'PONG';
  payload: unknown;
  timestamp: number;
}

interface CharacterPresence {
  characterId: number;
  characterName: string;
  systemId?: number;
  shipTypeId?: number;
  shipTypeName?: string;
  isOnline: boolean;
  lastSeen: number;
}

interface SyncPayload {
  systems: unknown[];
  connections: unknown[];
  signatures: unknown[];
  presence: CharacterPresence[];
  lastUpdated: number;
}

interface UpdatePayload {
  action: string;
  data: unknown;
  confirmed?: boolean;
  characterId?: number;
  characterName?: string;
}

export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface UseWebSocketOptions {
  mapId: string;
  onPresenceUpdate?: (presence: CharacterPresence[]) => void;
  onError?: (error: string) => void;
}

interface UseWebSocketReturn {
  status: ConnectionStatus;
  presence: CharacterPresence[];
  sendAction: (action: string, data: unknown) => void;
  reconnect: () => void;
}

const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000]; // Exponential backoff
const PING_INTERVAL = 30000; // 30 seconds
const PONG_TIMEOUT = 10000; // 10 seconds to receive pong

export function useWebSocket(options: UseWebSocketOptions): UseWebSocketReturn {
  const { mapId, onPresenceUpdate, onError } = options;
  const wsRef = useRef<WebSocket | null>(null);
  const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pongTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [presence, setPresence] = useState<CharacterPresence[]>([]);

  const { character, isAuthenticated, isLoading } = useAuthStore();
  const {
    setMapState,
    applyUpdate,
    confirmUpdate,
    setPresence: setStorePresence,
  } = useMapStore();

  // Build WebSocket URL
  const getWebSocketUrl = useCallback(() => {
    return wsUrl(`/api/maps/${mapId}/ws`);
  }, [mapId]);

  // Send a message through the WebSocket
  const sendMessage = useCallback((msg: WSMessage) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  // Send an action to the server
  const sendAction = useCallback(
    (action: string, data: unknown) => {
      sendMessage({
        type: 'UPDATE',
        payload: { action, data },
        timestamp: Date.now(),
      });
    },
    [sendMessage]
  );

  // Start ping/pong keepalive
  const startKeepalive = useCallback(() => {
    // Clear any existing intervals
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }

    pingIntervalRef.current = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        sendMessage({ type: 'PING', payload: null, timestamp: Date.now() });

        // Set timeout for pong response
        pongTimeoutRef.current = setTimeout(() => {
          console.warn('Pong timeout - connection may be dead');
          wsRef.current?.close();
        }, PONG_TIMEOUT);
      }
    }, PING_INTERVAL);
  }, [sendMessage]);

  // Stop keepalive
  const stopKeepalive = useCallback(() => {
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }
    if (pongTimeoutRef.current) {
      clearTimeout(pongTimeoutRef.current);
      pongTimeoutRef.current = null;
    }
  }, []);

  // Handle incoming messages
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data) as WSMessage;

        switch (msg.type) {
          case 'SYNC': {
            // Full state sync
            const payload = msg.payload as SyncPayload;
            setMapState(payload);
            setPresence(payload.presence || []);
            setStorePresence(payload.presence || []);
            onPresenceUpdate?.(payload.presence || []);
            break;
          }

          case 'UPDATE': {
            // Incremental update
            const payload = msg.payload as UpdatePayload;
            if (payload.confirmed) {
              // Server confirmed our optimistic update
              confirmUpdate(payload.action, payload.data);
            } else {
              // Update from another client
              applyUpdate(payload.action, payload.data);
            }
            break;
          }

          case 'PRESENCE': {
            // Presence update
            const presenceData = msg.payload as CharacterPresence[];
            setPresence(presenceData);
            setStorePresence(presenceData);
            onPresenceUpdate?.(presenceData);
            break;
          }

          case 'PONG': {
            // Clear pong timeout
            if (pongTimeoutRef.current) {
              clearTimeout(pongTimeoutRef.current);
              pongTimeoutRef.current = null;
            }
            break;
          }

          case 'ERROR': {
            const errorPayload = msg.payload as { message: string };
            console.error('WebSocket error from server:', errorPayload.message);
            onError?.(errorPayload.message);
            break;
          }

          default:
            console.warn('Unknown message type:', msg.type);
        }
      } catch (error) {
        console.error('Failed to parse WebSocket message:', error);
      }
    },
    [setMapState, applyUpdate, confirmUpdate, setStorePresence, onPresenceUpdate, onError]
  );

  // Connect to WebSocket
  const connect = useCallback(() => {
    // Don't connect if mapId is empty
    if (!mapId) {
      console.log('No mapId provided, skipping WebSocket connection');
      return;
    }

    // Wait for auth check to complete and verify user is authenticated
    if (isLoading) {
      console.log('Waiting for auth check to complete...');
      return;
    }

    if (!isAuthenticated || !character) {
      console.warn('Cannot connect to WebSocket without authentication');
      return;
    }

    // If there's already an open or connecting WebSocket, don't create another
    if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
      console.log('WebSocket already exists, skipping connection');
      return;
    }

    // Clean up any existing closed WebSocket
    if (wsRef.current) {
      wsRef.current = null;
    }

    setStatus('connecting');

    const url = getWebSocketUrl();
    const ws = new WebSocket(url);

    ws.onopen = () => {
      console.log('WebSocket connected to map:', mapId);
      setStatus('connected');
      reconnectAttemptRef.current = 0;
      startKeepalive();
    };

    ws.onmessage = handleMessage;

    ws.onclose = (event) => {
      console.log('WebSocket closed:', event.code, event.reason);
      setStatus('disconnected');
      stopKeepalive();

      // Attempt to reconnect if not a clean close
      if (event.code !== 1000) {
        const delay = RECONNECT_DELAYS[Math.min(reconnectAttemptRef.current, RECONNECT_DELAYS.length - 1)];
        console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptRef.current + 1})`);

        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptRef.current++;
          connect();
        }, delay);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      setStatus('error');
      onError?.('WebSocket connection error');
    };

    wsRef.current = ws;
  }, [character, isAuthenticated, isLoading, mapId, getWebSocketUrl, handleMessage, startKeepalive, stopKeepalive, onError]);

  // Manual reconnect
  const reconnect = useCallback(() => {
    reconnectAttemptRef.current = 0;
    connect();
  }, [connect]);

  // Track last mapId to detect changes
  const lastMapIdRef = useRef<string | null>(null);
  // Track if we've initiated a connection for the current mapId
  const connectionInitiatedRef = useRef(false);

  // Single consolidated effect for WebSocket connection management
  useEffect(() => {
    // Skip if no mapId, not authenticated, or still loading
    if (!mapId || isLoading || !isAuthenticated || !character) {
      return;
    }

    // If mapId changed, close existing connection and reset state
    if (lastMapIdRef.current !== mapId) {
      if (wsRef.current) {
        wsRef.current.close(1000, 'Map changed');
        wsRef.current = null;
      }
      connectionInitiatedRef.current = false;
      lastMapIdRef.current = mapId;
    }

    // Only connect if we haven't already initiated a connection for this mapId
    if (!connectionInitiatedRef.current && !wsRef.current) {
      connectionInitiatedRef.current = true;
      connect();
    }

    return () => {
      // Clear reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }

      // Stop keepalive
      stopKeepalive();

      // Close WebSocket on unmount
      if (wsRef.current) {
        wsRef.current.close(1000, 'Component unmounted');
        wsRef.current = null;
      }

      // Reset connection tracking on unmount
      connectionInitiatedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId, isLoading, isAuthenticated, character]);

  return {
    status,
    presence,
    sendAction,
    reconnect,
  };
}

export default useWebSocket;
